/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useEffect, useRef, useState } from 'react'

import { Link, useNavigate } from 'react-router-dom'

import { Alert, PageContainer, PageHeader } from '@/components/ui'
import { useApi } from '@/hooks'
import { CompanyDropdown } from '@/insights/components/CompanyDropdown'
import { getAuthProviderLabel } from '@/utils/authProvider'
import logger from '@/utils/logger'

import { CreateUserPanel } from './CreateUserPanel'
import { DashboardSeedPanel } from './DashboardSeedPanel'
import type { SeedSelection, UserSearchRow } from './types'

const log = logger({ category: 'AdminNewUserPage' })

const MIN_QUERY = 2

const fullName = (row: UserSearchRow) => [row.firstName, row.lastName].filter(Boolean).join(' ')

/** Email, else name, else id — imported SSO accounts can lack an email. */
const headline = (row: UserSearchRow) => row.email || fullName(row) || row.userName || row.uid || ''

/** Name, username and company, skipping whatever the headline already shows. */
const details = (row: UserSearchRow) =>
  [fullName(row), row.userName, row.companyName].filter((v) => v && v !== headline(row))

const lastSignIn = (row: UserSearchRow) => (row.lastLogin ? new Date(row.lastLogin).toLocaleDateString() : 'Never')

/** A row can be provisioned when it has a (current or future) login identity and no profile yet. */
const grantable = (row: UserSearchRow) => !row.hasProfile && row.uid !== null

const Check: React.FC<{ present: boolean }> = ({ present }) =>
  present ? <span className="text-success">✓</span> : <span className="text-error">✗</span>

/** Plain-language reading of the store badges, shown as a hover tooltip. */
const storeExplanation = (row: UserSearchRow): string => {
  if (row.uid === null) {
    return 'Has a Fresh record but no login account — this person cannot sign in.'
  }
  const sso = !row.uid.startsWith('auth0|')
  if (row.inFresh && row.inAuth0) {
    return sso
      ? 'Has signed in with their company (SSO) account; a Fresh account with the same email also exists.'
      : 'Has a Fresh account and has signed in with it before.'
  }
  if (row.inFresh) {
    return 'Has a Fresh account but has never signed in, so no Auth0 record exists yet — one appears automatically at their first sign-in.'
  }
  return sso
    ? 'Has signed in with their company (SSO) account but has no Fresh account.'
    : 'Has an Auth0 login but no matching Fresh account.'
}

/** "Fresh ✓ Auth0 ✓ (Active Directory)" — per-store presence, plus the IdP for SSO identities. */
const StoreBadges: React.FC<{ row: UserSearchRow }> = ({ row }) => {
  const idp = row.uid && !row.uid.startsWith('auth0|') ? getAuthProviderLabel(row.uid) : ''
  return (
    <span
      className="tooltip tooltip-right tooltip-text-left cursor-help text-xs text-base-content/60"
      data-tip={storeExplanation(row)}
      aria-label={`Fresh: ${row.inFresh ? 'yes' : 'no'}, Auth0: ${row.inAuth0 ? 'yes' : 'no'}`}
    >
      Fresh <Check present={row.inFresh} /> <span className="ml-1">Auth0</span> <Check present={row.inAuth0} />
      {idp && <span> ({idp})</span>}
    </span>
  )
}

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-base-content/50">{label}</div>
    <div className="min-h-6">{value || <span className="text-base-content/40">—</span>}</div>
  </div>
)

/**
 * Provision an Insights profile. One search covers both user stores, merged one
 * row per identity — users who already have access are shown too (with a link
 * to their page) so the admin never has to guess which system someone is in.
 * Rows with a freshUserId provision through the Fresh path, which parks the
 * permission grant for the login Action to assign at first sign-in.
 */
const AdminNewUserPage: React.FC = () => {
  const { api } = useApi()
  const navigate = useNavigate()

  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [auth0Unavailable, setAuth0Unavailable] = useState(false)
  const [results, setResults] = useState<UserSearchRow[] | null>(null)
  const [activeIdx, setActiveIdx] = useState(-1)
  const [candidate, setCandidate] = useState<UserSearchRow | null>(null)
  const [creating, setCreating] = useState(false)
  const [seed, setSeed] = useState<SeedSelection>({ mode: 'default' })
  const [grantCompanyId, setGrantCompanyId] = useState<number>()
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [saveErrorCode, setSaveErrorCode] = useState(0)

  // Autocomplete: search as the admin types, debounced so a burst of keystrokes
  // costs one round-trip. The sequence counter drops responses that come back
  // after the query has moved on.
  const searchSeq = useRef(0)
  useEffect(() => {
    const q = query.trim()
    const seq = ++searchSeq.current
    setActiveIdx(-1)
    if (q.length < MIN_QUERY) {
      setResults(null)
      setSearchError('')
      setAuth0Unavailable(false)
      setSearching(false)
      return
    }
    setSearching(true)
    const handle = setTimeout(() => {
      api
        .get<UserSearchRow[]>({
          path: `/app/admin/user-search?q=${encodeURIComponent(q)}`,
          origin: 'AdminNewUserPage#search',
        })
        .then((response) => {
          if (seq !== searchSeq.current) return
          if (response.error) {
            setResults(null)
            setSearchError(response.message || 'Could not search users')
          } else {
            setSearchError('')
            setResults(response.data ?? [])
            setAuth0Unavailable((response as { auth0Unavailable?: boolean }).auth0Unavailable ?? false)
          }
          setSearching(false)
        })
        .catch((ex: unknown) => {
          log.error(ex)
          if (seq !== searchSeq.current) return
          setSearchError('Could not search users')
          setSearching(false)
        })
    }, 350)
    return () => clearTimeout(handle)
  }, [query, api])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!results?.length) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      const row = results[activeIdx]
      if (grantable(row)) pick(row)
    } else if (e.key === 'Escape') {
      setResults(null)
    }
  }

  const pick = (row: UserSearchRow) => {
    setCandidate(row)
    setSeed({ mode: 'default' })
    setGrantCompanyId(undefined)
    setSaveError('')
    setSaveErrorCode(0)
  }

  const save = async () => {
    if (!candidate) return
    setSaving(true)
    setSaveError('')
    setSaveErrorCode(0)
    try {
      const copying = seed.mode === 'copy' && seed.sourceUid && (seed.dashboardSlugs?.length ?? 0) > 0
      const seedBody = copying ? { sourceUid: seed.sourceUid, dashboardSlugs: seed.dashboardSlugs } : {}
      // Anyone in Fresh provisions through the Fresh path — it parks the
      // permission grant; the plain Auth0 path assumes permissions exist.
      const response =
        candidate.freshUserId !== null
          ? await api.post<{ uid: string }, object>({
              path: '/app/admin/users/fresh',
              origin: 'AdminNewUserPage#createFresh',
              body: { freshUserId: candidate.freshUserId, ...seedBody },
            })
          : await api.post<{ uid: string }, object>({
              path: '/app/admin/users',
              origin: 'AdminNewUserPage#create',
              body: {
                uid: candidate.uid,
                ...(grantCompanyId !== undefined ? { companyId: grantCompanyId } : {}),
                ...seedBody,
              },
            })
      if (response.error) {
        setSaveError(response.message || 'Could not create the profile')
        setSaveErrorCode(response.code)
        return
      }
      const uid = response.data?.uid ?? candidate.uid ?? ''
      navigate(`/admin/users/${encodeURIComponent(uid)}`)
    } catch (ex) {
      log.error(ex)
      setSaveError('Could not create the profile')
    } finally {
      setSaving(false)
    }
  }

  const canSave =
    !!candidate &&
    !saving &&
    // SSO users with no Fresh row have no company to inherit — the admin picks one.
    (candidate.inFresh || grantCompanyId !== undefined) &&
    (seed.mode === 'default' || (!!seed.sourceUid && (seed.dashboardSlugs?.length ?? 0) > 0))

  return (
    <PageContainer>
      <Link to="/admin/users" className="link link-hover mb-2 inline-block text-sm">
        ← Users
      </Link>
      <PageHeader title="Add User" subtitle="Provision an Insights profile" />

      {creating && !candidate && <CreateUserPanel onCancel={() => setCreating(false)} />}

      {!creating && !candidate && (
        // Narrow enough that the help text wraps at a clean break instead of
        // stranding its tail ("3).") alone on the second line.
        <section className="flex max-w-[500px] flex-col gap-3">
          <p className="text-sm text-base-content/70">
            Search for an existing Fresh or Auth0 user to grant permission to Insights. If the user is completely new,
            click the Create New User button below to add a new user and grant permission to Insights.
          </p>
          <input
            className="input input-bordered input-sm w-full"
            placeholder="Type an email, name or username"
            value={query}
            aria-label="Search users"
            autoComplete="off"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <p className="text-sm text-base-content/60">
            {searching
              ? 'Searching…'
              : `At least ${String(MIN_QUERY)} characters. Searches Fresh and Auth0 by email, name or username (Auth0 matching starts at 3).`}
          </p>

          {searchError && <Alert type="error" message={searchError} />}
          {auth0Unavailable && (
            <Alert type="warning" message="Auth0 could not be reached — showing Fresh results only." />
          )}

          {results && results.length === 0 && <Alert type="info" message={`No users match '${query.trim()}'.`} />}

          <div>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setCreating(true)}>
              Create New User
            </button>
          </div>

          {results && results.length > 0 && (
            <ul
              role="listbox"
              aria-label="Users"
              className="divide-y divide-base-300 rounded-lg border border-base-300 bg-base-100 shadow"
            >
              {results.map((row, i) => (
                <li
                  key={row.uid ?? `fresh:${String(row.freshUserId)}`}
                  role="option"
                  aria-selected={i === activeIdx}
                  aria-disabled={!grantable(row)}
                  className={`flex items-center justify-between gap-3 px-3 py-2 text-sm ${
                    i === activeIdx ? 'bg-base-200' : ''
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate">
                      {headline(row)}
                      {row.blocked && <span className="badge badge-error badge-sm ml-2">blocked</span>}
                    </span>
                    {details(row).length > 0 && (
                      <span className="block truncate text-xs text-base-content/60">{details(row).join(' · ')}</span>
                    )}
                    <span className="block">
                      <StoreBadges row={row} />
                    </span>
                    <span className="block text-xs text-base-content/50">{`Last Sign-in: ${lastSignIn(row)}`}</span>
                    {/* The raw id, for matching the row up against Auth0. */}
                    {row.uid && (
                      <span className="block truncate font-mono text-[11px] text-base-content/40">{row.uid}</span>
                    )}
                  </span>
                  <span className="shrink-0">
                    {row.hasProfile && row.uid ? (
                      <Link className="link link-primary" to={`/admin/users/${encodeURIComponent(row.uid)}`}>
                        View profile
                      </Link>
                    ) : grantable(row) ? (
                      <button type="button" className="btn btn-outline btn-xs" onClick={() => pick(row)}>
                        Grant access
                      </button>
                    ) : (
                      <span className="text-xs text-base-content/60">No login account</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {candidate && (
        <section className="flex max-w-2xl flex-col gap-5">
          <div className="rounded-lg bg-base-100 p-4 shadow">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-medium">User</h2>
              <button type="button" className="btn btn-ghost btn-xs" onClick={() => setCandidate(null)}>
                Change user
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              <Field label="Email" value={candidate.email} />
              <Field label="First name" value={candidate.firstName} />
              <Field label="Last name" value={candidate.lastName} />
              <Field label="Username" value={candidate.userName} />
              <Field label="Company" value={candidate.companyName} />
              <Field label="Last sign-in" value={lastSignIn(candidate)} />
            </div>
            <p className="mt-3">
              <StoreBadges row={candidate} />
            </p>
            {candidate.uid && <p className="mt-1 break-all font-mono text-xs text-base-content/50">{candidate.uid}</p>}
            <p className="mt-3 text-sm text-base-content/60">
              These fields come from {candidate.freshUserId !== null ? 'Fresh' : 'Auth0'} and are refreshed at every
              login, so they cannot be edited here. This profile grants Insights access as soon as it is created.
            </p>
            {!candidate.inFresh && (
              <div className="mt-3 max-w-md">
                <CompanyDropdown
                  value={grantCompanyId}
                  onChange={(id) => setGrantCompanyId(Array.isArray(id) ? id[0] : id)}
                />
                <p className="mt-1 text-sm text-base-content/60">
                  Required — this user has no Fresh record to inherit a company from.
                </p>
              </div>
            )}
          </div>

          <div className="rounded-lg bg-base-100 p-4 shadow">
            <h2 className="mb-3 font-medium">Dashboards</h2>
            <DashboardSeedPanel
              // Fresh users' company comes with the row; non-Fresh grants use
              // whatever the admin has picked so far.
              targetCompanyId={candidate.inFresh ? (candidate.companyId ?? undefined) : grantCompanyId}
              onChange={setSeed}
            />
          </div>

          {saveError && (
            <Alert
              type="error"
              message={
                saveErrorCode === 409 && /already exists/i.test(saveError)
                  ? `${saveError} — view it at Admin → Users.`
                  : saveError
              }
            />
          )}

          <div className="flex items-center gap-2">
            <button type="button" className="btn btn-primary btn-sm" disabled={!canSave} onClick={() => void save()}>
              {saving ? 'Creating…' : 'Create profile'}
            </button>
            <Link to="/admin/users" className="btn btn-ghost btn-sm">
              Cancel
            </Link>
          </div>
        </section>
      )}
    </PageContainer>
  )
}

export default AdminNewUserPage
